import { useCallback, useRef, useState } from 'react';
import axios from 'axios';

const DEFAULT_ICE = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

/** Normalize SDP from WS/JSON into RTCSessionDescriptionInit. */
export function normalizeSdp(desc) {
  if (!desc) return null;
  let value = desc;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch (_) {
      return null;
    }
  }
  if (!value || typeof value !== 'object') return null;
  const type = value.type;
  if (!type || typeof value.sdp !== 'string') return null;
  return { type, sdp: value.sdp };
}

function toSignalSdp(desc) {
  if (!desc) return null;
  return { type: desc.type, sdp: desc.sdp };
}

async function fetchIceServers() {
  try {
    const res = await axios.get('/api/conversations/turn-credentials');
    const servers = res.data?.iceServers;
    if (Array.isArray(servers) && servers.length > 0) {
      return servers;
    }
  } catch (_) {
    /* fall through to STUN-only */
  }
  return DEFAULT_ICE;
}

/**
 * Low-level WebRTC helpers used by CallContext.
 * Keeps peer connection + local media lifecycle out of React render paths.
 */
export function createWebRTCController({ sendSignal, onRemoteStream, onConnectionState }) {
  let pc = null;
  let localStream = null;
  let callType = 'voice';
  const pendingCandidates = [];

  const stopTracks = (stream) => {
    if (!stream) return;
    stream.getTracks().forEach((t) => {
      try {
        t.stop();
      } catch (_) {
        /* ignore */
      }
    });
  };

  const cleanup = () => {
    if (pc) {
      try {
        pc.onicecandidate = null;
        pc.ontrack = null;
        pc.onconnectionstatechange = null;
        pc.oniceconnectionstatechange = null;
        pc.close();
      } catch (_) {
        /* ignore */
      }
      pc = null;
    }
    stopTracks(localStream);
    localStream = null;
    pendingCandidates.length = 0;
  };

  const reportConnectionState = (state) => {
    if (!state) return;
    onConnectionState?.(state);
  };

  const ensurePeer = async () => {
    if (pc) return pc;
    const iceServers = await fetchIceServers();
    pc = new RTCPeerConnection({ iceServers });

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        sendSignal('ice_candidate', { candidate: event.candidate.toJSON() });
      }
    };

    pc.ontrack = (event) => {
      const [stream] = event.streams;
      if (stream) onRemoteStream?.(stream);
    };

    // Prefer connectionState; fall back to iceConnectionState (Safari / older Chromium)
    pc.onconnectionstatechange = () => {
      reportConnectionState(pc?.connectionState || 'closed');
    };
    pc.oniceconnectionstatechange = () => {
      const ice = pc?.iceConnectionState;
      if (ice === 'connected' || ice === 'completed') {
        reportConnectionState('connected');
      } else if (ice === 'failed') {
        reportConnectionState('failed');
      } else if (ice === 'disconnected') {
        reportConnectionState('disconnected');
      }
    };

    return pc;
  };

  const getLocalMedia = async (type) => {
    callType = type === 'video' ? 'video' : 'voice';
    const constraints = {
      audio: true,
      video: callType === 'video' ? { facingMode: 'user' } : false,
    };
    try {
      localStream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      // Camera denied → voice-only fallback
      if (callType === 'video') {
        callType = 'voice';
        localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      } else {
        throw err;
      }
    }
    return localStream;
  };

  const attachLocalTracks = async (peer) => {
    if (!localStream) return;
    localStream.getTracks().forEach((track) => {
      peer.addTrack(track, localStream);
    });
  };

  const flushCandidates = async () => {
    if (!pc || !pc.remoteDescription) return;
    while (pendingCandidates.length) {
      const c = pendingCandidates.shift();
      try {
        await pc.addIceCandidate(c);
      } catch (_) {
        /* ignore bad candidates */
      }
    }
  };

  return {
    getLocalStream: () => localStream,
    getCallType: () => callType,
    getPeerConnection: () => pc,

    async startOffer(type) {
      cleanup();
      const stream = await getLocalMedia(type);
      const peer = await ensurePeer();
      await attachLocalTracks(peer);
      const offer = await peer.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: type === 'video' || callType === 'video',
      });
      await peer.setLocalDescription(offer);
      return {
        offerSdp: toSignalSdp(peer.localDescription),
        localStream: stream,
        effectiveType: callType,
      };
    },

    async acceptOffer(offerSdp, type) {
      cleanup();
      const remote = normalizeSdp(offerSdp);
      if (!remote) {
        throw new Error('Invalid remote offer SDP');
      }
      const stream = await getLocalMedia(type);
      const peer = await ensurePeer();
      await attachLocalTracks(peer);
      await peer.setRemoteDescription(remote);
      await flushCandidates();
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);
      return {
        answerSdp: toSignalSdp(peer.localDescription),
        localStream: stream,
        effectiveType: callType,
      };
    },

    async applyAnswer(answerSdp) {
      if (!pc) {
        throw new Error('No peer connection for answer');
      }
      const remote = normalizeSdp(answerSdp);
      if (!remote) {
        throw new Error('Invalid remote answer SDP');
      }
      await pc.setRemoteDescription(remote);
      await flushCandidates();
    },

    async addIceCandidate(candidate) {
      if (!candidate) return;
      let init = candidate;
      if (typeof init === 'string') {
        try {
          init = JSON.parse(init);
        } catch (_) {
          return;
        }
      }
      if (!init.candidate && init.candidate !== '') return;
      const ice = init instanceof RTCIceCandidate ? init : new RTCIceCandidate(init);
      if (!pc || !pc.remoteDescription) {
        pendingCandidates.push(ice);
        return;
      }
      try {
        await pc.addIceCandidate(ice);
      } catch (_) {
        /* ignore */
      }
    },

    setMicEnabled(enabled) {
      localStream?.getAudioTracks().forEach((t) => {
        t.enabled = !!enabled;
      });
    },

    setCameraEnabled(enabled) {
      localStream?.getVideoTracks().forEach((t) => {
        t.enabled = !!enabled;
      });
    },

    async switchCamera() {
      if (!localStream || callType !== 'video') return localStream;
      const videoTrack = localStream.getVideoTracks()[0];
      if (!videoTrack) return localStream;
      const current = videoTrack.getSettings().facingMode || 'user';
      const nextFacing = current === 'user' ? 'environment' : 'user';
      try {
        const next = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { exact: nextFacing } },
          audio: false,
        });
        const newTrack = next.getVideoTracks()[0];
        const sender = pc?.getSenders().find((s) => s.track && s.track.kind === 'video');
        if (sender && newTrack) {
          await sender.replaceTrack(newTrack);
        }
        videoTrack.stop();
        localStream.removeTrack(videoTrack);
        localStream.addTrack(newTrack);
        next.getAudioTracks().forEach((t) => t.stop());
      } catch (_) {
        /* device may not support facingMode */
      }
      return localStream;
    },

    async restartIce() {
      if (!pc) return null;
      try {
        const offer = await pc.createOffer({ iceRestart: true });
        await pc.setLocalDescription(offer);
        return pc.localDescription;
      } catch (_) {
        return null;
      }
    },

    cleanup,
  };
}

/** React hook wrapper exposing stream state for CallScreen. */
export default function useWebRTC() {
  const controllerRef = useRef(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [connectionState, setConnectionState] = useState('new');

  const resetStreams = useCallback(() => {
    setLocalStream(null);
    setRemoteStream(null);
    setConnectionState('new');
  }, []);

  return {
    controllerRef,
    localStream,
    remoteStream,
    connectionState,
    setLocalStream,
    setRemoteStream,
    setConnectionState,
    resetStreams,
    createController: createWebRTCController,
  };
}
